/** Formatadores puros para o componente de viagens. */
import type { GenUiLocale } from '../ui/i18n.js';

type LocaleInput = string | null | undefined;

function displayLocale(locale: LocaleInput): GenUiLocale {
  const normalized = locale?.replaceAll('_', '-').toLowerCase();
  if (normalized?.startsWith('en')) return 'en';
  if (normalized?.startsWith('es')) return 'es';
  return 'pt-BR';
}

// Moeda NÃO é localizada: formato brasileiro fixo (R$ 1.234,56) com o código de
// moeda do backend, independente do locale da UI. Só texto e datas seguem o
// idioma — ver `fmtDateRange`/`fmtDateTime` abaixo, que recebem `locale`.
export function fmtMoney(amount: number | null, currency: string | null): string | null {
  if (amount == null || !currency) return null;
  try {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

const MONTHS_PT = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
];
const MONTHS_EN = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const MONTHS_ES = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic',
];

type DateParts = { y: number; mo: number; d: number; hh?: string; mm?: string };

/**
 * Extrai ano/mês/dia (e hora opcional) do head ISO `YYYY-MM-DD[THH:MM]` via
 * regex. NÃO usa `Date` — evita o deslocamento de fuso que reescreveria o
 * relógio de parede. null quando a string não começa com um head ISO (ex.: já
 * vem formatada como "10 out").
 */
function parseParts(s: string | null | undefined): DateParts | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(s);
  if (!m) return null;
  return { y: Number(m[1]), mo: Number(m[2]), d: Number(m[3]), hh: m[4], mm: m[5] };
}

function monthLabels(locale: LocaleInput): string[] {
  const display = displayLocale(locale);
  switch (display) {
    case 'en':
      return MONTHS_EN;
    case 'es':
      return MONTHS_ES;
    case 'pt-BR':
      return MONTHS_PT;
    default: {
      const exhaustive: never = display;
      return exhaustive;
    }
  }
}

function mon(p: DateParts, locale: LocaleInput): string {
  return monthLabels(locale)[p.mo - 1] ?? String(p.mo);
}

function full(p: DateParts, locale: LocaleInput): string {
  return `${String(p.d)} ${mon(p, locale)} ${String(p.y)}`;
}

function sameDay(a: DateParts, b: DateParts): boolean {
  return a.y === b.y && a.mo === b.mo && a.d === b.d;
}

/**
 * Intervalo de datas formatado, espelhando as regras do nome da viagem no
 * backend (`deriveTripName`): mesmo dia / sem fim → "17 jun 2026"; mesmo mês →
 * "10–20 jun 2026"; mesmo ano → "30 jun–2 jul 2026"; senão completo–completo.
 *
 * Os campos `*Raw` chegam verbatim do backend (`raw()` devolve a string da fonte
 * ou um `toISOString()` p/ `Date` BSON), logo um ISO precisa ser FORMATADO, não
 * ecoado. Tenta o head ISO de `*Raw` (relógio de parede fiel), com fallback ao
 * ISO `*At`. null quando nenhum dos lados parseia.
 */
export function fmtDateRange(
  startRaw: string | null | undefined,
  endRaw: string | null | undefined,
  startIso?: string | null,
  endIso?: string | null,
  locale: LocaleInput = 'pt-BR',
): string | null {
  const start = parseParts(startRaw) ?? parseParts(startIso);
  if (!start) return startRaw ?? startIso ?? null;
  const end = parseParts(endRaw) ?? parseParts(endIso) ?? start;
  if (sameDay(start, end)) return full(start, locale);
  if (start.y === end.y && start.mo === end.mo)
    return `${String(start.d)}–${String(end.d)} ${mon(end, locale)} ${String(end.y)}`;
  if (start.y === end.y)
    return `${String(start.d)} ${mon(start, locale)}–${String(end.d)} ${mon(end, locale)} ${String(end.y)}`;
  return `${full(start, locale)}–${full(end, locale)}`;
}

/**
 * Formata uma data-hora para "10 out 2026 · 08h20". Aceita o `raw` (verbatim do
 * backend — pode ser um ISO ou uma string já formatada) e o `iso`. Extrai o head
 * ISO de `raw`, senão de `iso`, e formata; se `raw` não for ISO (já formatado),
 * devolve `raw` como está. null quando nenhum dos dois serve.
 */
export function fmtDateTime(
  raw: string | null | undefined,
  iso: string | null | undefined,
  locale: LocaleInput = 'pt-BR',
): string | null {
  const p = parseParts(raw) ?? parseParts(iso);
  if (!p) return raw ?? null;
  if (p.hh == null || p.mm == null) return full(p, locale);
  const time = displayLocale(locale) === 'pt-BR' ? `${p.hh}h${p.mm}` : `${p.hh}:${p.mm}`;
  return `${full(p, locale)} · ${time}`;
}

/** Compõe "aeroporto/estação · data · hora" dropando os pedaços ausentes. */
export function fmtPlaceAt(place: string | null | undefined, at: string | null): string | null {
  const parts = [place, at].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}
